import * as React from 'react';
import type { strokeDashTypes } from 'react-financial-charts';
import { isDefined, getMouseCanvas, GenericChartComponent, getStrokeDasharrayCanvas } from 'react-financial-charts';
import { generateLine, isHovering } from './index';
import { getFillStyleWithOpacity } from '@/utils/rgbTorgba';
import type { MoreProps, MorePropsChartConfig } from '@/types/commontypes';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;

export interface ChannelWithAreaProps {
  readonly startXY?: number[];
  readonly endXY?: number[];
  readonly dy?: number;
  readonly interactiveCursorClass?: string;
  readonly strokeStyle: string | undefined;
  readonly strokeWidth: number;
  readonly strokeDasharray: strokeDashTypes;
  readonly fillStyle: string;
  readonly type:
    | 'XLINE' // extends from -Infinity to +Infinity
    | 'RAY' // extends to +/-Infinity in one direction
    | 'LINE'; // extends between the set bounds
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly defaultClassName?: string;
  readonly tolerance: number;
  readonly selected: boolean;
  readonly extendLeft: boolean;
  readonly extendRight: boolean;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly opacity?: number | undefined;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class ChannelWithArea extends React.Component<ChannelWithAreaProps> {
  public static defaultProps = {
    type: 'LINE',
    strokeWidth: 1,
    tolerance: 4,
    selected: false,
  };

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover, onDoubleClick: _onDoubleClick } = this.props;

    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={this.handleDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const {
      fillStyle,
      opacity,
      strokeStyle,
      strokeWidth,
      strokeDasharray,
      extendLeft: _extendLeft,
      extendRight: _extendRight,
    } = this.props;
    const { line1, line2 } = helper(this.props, moreProps);
    if (line1 !== undefined) {
      const { x1, y1, x2, y2 } = line1;
      ctx.lineWidth = strokeWidth;
      ctx.strokeStyle = strokeStyle!;
      const lineDash = getStrokeDasharrayCanvas(strokeDasharray);

      ctx.setLineDash(lineDash);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      if (line2 !== undefined) {
        const { y1: line2Y1, y2: line2Y2 } = line2;
        const finalFillStyle = getFillStyleWithOpacity(fillStyle, opacity);
        ctx.beginPath();
        ctx.moveTo(x1, line2Y1);
        ctx.lineTo(x2, line2Y2);
        ctx.stroke();
        ctx.fillStyle = finalFillStyle;

        ctx.beginPath();
        ctx.moveTo(x1, y1);

        ctx.lineTo(x2, y2);
        ctx.lineTo(x2, line2Y2);
        ctx.lineTo(x1, line2Y1);

        ctx.closePath();
        ctx.fill();
      }
    }
  };
  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };
  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };
  private readonly isHover = (moreProps: MoreProps) => {
    const { tolerance, onHover, startXY, endXY, dy } = this.props;

    if (onHover !== undefined) {
      const { line1, line2 } = helper(this.props, moreProps);

      if (line1 !== undefined && line2 !== undefined) {
        const {
          mouseXY,
          xScale,
          chartConfig: { yScale },
        } = moreProps;

        const line1Hovering = isHovering({
          x1Value: startXY && dy !== undefined ? startXY[0] : undefined,
          y1Value: startXY && dy !== undefined ? startXY[1] + dy : undefined,
          x2Value: endXY && dy !== undefined ? endXY[0] : undefined,
          y2Value: endXY && dy !== undefined ? endXY[1] + dy : undefined,
          type: 'LINE',
          mouseXY,
          tolerance,
          xScale,
          yScale,
        });

        const line2Hovering = isHovering({
          x1Value: startXY?.[0],
          y1Value: startXY?.[1],
          x2Value: endXY?.[0],
          y2Value: endXY?.[1],
          type: 'LINE',
          mouseXY,
          tolerance,
          xScale,
          yScale,
        });
        return line1Hovering || line2Hovering;
      }
    }
    return false;
  };
  private readonly handleDoubleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.isHover(moreProps)) {
      const { onDoubleClick } = this.props;
      if (onDoubleClick) {
        onDoubleClick(e, moreProps);
      }
    }
  };
}

interface LineCoords {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function getLines(props: ChannelWithAreaProps, moreProps: MoreProps) {
  const { startXY, endXY, dy, type, extendLeft, extendRight } = props;
  if (!isDefined(startXY) || !isDefined(endXY)) {
    return {};
  }

  const realYScale = moreProps.chartConfig.yScale;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const noop_yScale = Object.assign((v: number) => v, {
    domain: () => realYScale.domain(),
  }) as MorePropsChartConfig['yScale'];

  const line1Raw = generateLine({
    type,
    start: startXY,
    end: endXY,
    xScale: moreProps.xScale,
    yScale: noop_yScale,
    extendLeft,
    extendRight,
  });

  const line1: LineCoords = {
    x1: line1Raw.x1,
    y1: line1Raw.y1,
    x2: line1Raw.x2,
    y2: line1Raw.y2,
  };

  const line2: LineCoords | undefined = isDefined(dy)
    ? {
        ...line1,
        y1: line1.y1 + dy,
        y2: line1.y2 + dy,
      }
    : undefined;

  return {
    line1,
    line2,
  };
}

function helper(props: ChannelWithAreaProps, moreProps: MoreProps) {
  const lines = getLines(props, moreProps);
  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  const line1 =
    lines.line1 !== undefined
      ? {
          x1: xScale(lines.line1.x1),
          y1: yScale(lines.line1.y1),
          x2: xScale(lines.line1.x2),
          y2: yScale(lines.line1.y2),
        }
      : undefined;

  const line2 =
    lines.line2 !== undefined
      ? {
          x1: line1!.x1,
          y1: yScale(lines.line2.y1),
          x2: line1!.x2,
          y2: yScale(lines.line2.y2),
        }
      : undefined;

  return {
    lines,
    line1,
    line2,
  };
}
